/**
 * Discover redesign — hero-banner selection + paging logic (piece 1).
 *
 * PURE, DB-free, deterministic. Kept separate from the react-query hook so the
 * decisions ("what does the hero feature, and how does it page?") are unit-
 * assertable in isolation — the same split `showcaseLogic.js` already uses.
 *
 * Gordon, brainstorm round 1 + clarification 2026-07-31 (verbatim):
 *   "There is the top banner showcasing the trending Wheels as described in a
 *    carousel of 3-at-a-time if there is room/more than three. The main Grid has
 *    the standard Explore Wheel Cards..."
 *
 * So the hero is NOT three Wheels total — it shows THREE AT A TIME out of a
 * larger Trending pool and pages through the rest. The Wheel cards themselves are
 * the SHARED `ExploreWheelCard`, unchanged (Gordon, same day: "keep the Card
 * Style for the Wheels, don't redesign those") — the hero only makes them bigger
 * by giving each a third of the page instead of a grid cell.
 *
 * WHY THE THRESHOLD DIFFERS FROM THE SHOWCASE RAIL: the rail suppresses itself
 * below 8 eligible Wheels because "a 2-3 card rail reads as broken". A hero whose
 * whole shape is three-across is complete at 1, 2 or 3 — it hides only when there
 * is genuinely nothing to feature.
 */

// How many Wheels the hero shows AT ONCE on a full-width page. Narrower
// viewports pass a smaller `perPage` (the hero owns that responsive mapping).
export const HERO_PER_PAGE = 3;

// How deep the Trending pool behind the carousel goes. Matches the showcase
// rail's own top-N cap so both read the same slice of the scored order, and
// bounds the carousel at 4 pages of 3 — beyond that the main grid is the
// better affordance than more paging.
export const HERO_MAX_WHEELS = 12;

/**
 * gordon-0909-trending-eligibility-description-segments-portrait (Gordon
 * 2026-09-09 09:23 PT): a live Discover > Trending today screenshot showed
 * real wheels with "No description", one with the default iYuga-logo
 * portrait. Product rule: the hero may feature a wheel ONLY IF it has a real
 * description, a real portrait, and no empty segment — see
 * `trending_eligible` (get_explore_queryset's new annotation,
 * `yuga_wheel/api/v2/viewsets/wheel/actions/explore/explore.py`, Backend) for
 * the exact BE-side definition of each of those three conditions.
 *
 * DELIBERATELY the ONLY place this rule is enforced — the hero and the main
 * grid share the SAME fetched Trending pages (see this module's own header
 * comment / `trendingFeedParams.js`), so filtering it here (before the
 * carousel pool is built) rather than in the shared feed means the plain
 * Discover grid keeps rendering every wheel exactly as before; only the
 * hero's own slice shrinks.
 *
 * `trending_eligible !== false` (not `=== true`) so a wheel object that
 * predates this field (older cached page, a test fixture that never set it)
 * is treated as eligible — byte-identical to today's behaviour until the
 * backend actually annotates a wheel ineligible.
 *
 * @param {object} wheel
 * @returns {boolean}
 */
export function isTrendingEligible(wheel) {
  return wheel?.trending_eligible !== false;
}

/**
 * Decide the hero's render state from the (already-fetched) Trending feed.
 *
 * @param {object}  input
 * @param {Array}   input.wheels    flattened Trending feed items, page-ordered
 * @param {boolean} input.isLoading react-query isLoading (no data yet)
 * @param {boolean} input.isError   react-query isError
 * @returns {{ status: "loading"|"hidden"|"ready", items: Array }}
 *   - "loading": first load in flight, nothing cached → skeleton hero.
 *   - "hidden":  nothing to feature, or the feed errored with nothing
 *                cached → render nothing (the main feed owns the error
 *                surface; never duplicate it).
 *   - "ready":   render the hero over `items` (1..HERO_MAX_WHEELS).
 *
 * T22.435 (Kelly P0, Discover "Trending today" row vanished minutes after it
 * was on screen): react-query sets `isError` when a BACKGROUND refetch fails
 * (remount after the 60 s staleTime, reconnect) while the cached pages stay in
 * `data`. Hiding on any `isError` made the whole Trending row disappear with
 * the grid still rendering those same cached wheels. An error only hides the
 * hero when there is nothing cached to show — same rule as the grid.
 */
export function computeHeroState({ wheels, isLoading, isError } = {}) {
  const loaded = Array.isArray(wheels) ? wheels : [];

  if (isError && loaded.length === 0) return { status: "hidden", items: [] };

  if (isLoading && loaded.length === 0) return { status: "loading", items: [] };

  const eligible = loaded.filter(isTrendingEligible);

  if (eligible.length === 0) return { status: "hidden", items: [] };

  return { status: "ready", items: eligible.slice(0, HERO_MAX_WHEELS) };
}

/**
 * Resolve one page of the carousel.
 *
 * Wraps at both ends. Gordon 2026-09-16 (verbatim): "clicking the next batch of
 * Wheels should cycle back to the first batch". This replaces the earlier
 * non-wrapping design (controls disabled at the ends). A screen-reader user
 * still hears where they are: the hero's polite live region announces
 * "page 1 of N" after the wrap. So `hasPrev`/`hasNext` are true whenever there
 * is more than one page, and `stepHeroPage` does the wrapping arithmetic.
 *
 * @param {object} input
 * @param {Array}  input.items    the hero pool (from computeHeroState)
 * @param {number} input.page     zero-based page index (may be out of range)
 * @param {number} [input.perPage=HERO_PER_PAGE]
 * @returns {{ visible: Array, page: number, pageCount: number,
 *             hasPrev: boolean, hasNext: boolean }}
 */
export function heroPage({ items, page = 0, perPage = HERO_PER_PAGE } = {}) {
  const pool = Array.isArray(items) ? items : [];
  const size = Number.isFinite(perPage) && perPage > 0 ? Math.floor(perPage) : HERO_PER_PAGE;
  const pageCount = Math.ceil(pool.length / size);

  if (pageCount === 0) {
    return { visible: [], page: 0, pageCount: 0, hasPrev: false, hasNext: false };
  }

  // Clamp rather than trust: `perPage` shrinks on a viewport resize, which can
  // strand the current page index past the new end.
  const raw = Number.isFinite(page) ? Math.floor(page) : 0;
  const current = Math.min(Math.max(raw, 0), pageCount - 1);

  return {
    visible: pool.slice(current * size, current * size + size),
    page: current,
    pageCount,
    hasPrev: pageCount > 1,
    hasNext: pageCount > 1,
  };
}

/**
 * Step the carousel page index by `delta`, wrapping at both ends: Next on the
 * last page returns 0, Previous on the first page returns the last page.
 *
 * The stored index is clamped into range first, because a viewport resize can
 * strand it past the end (see heroPage) before the re-sync effect runs.
 *
 * @param {object} input
 * @param {number} input.page       current zero-based page index
 * @param {number} input.delta      +1 for Next, -1 for Previous
 * @param {number} input.pageCount  total pages (from heroPage)
 * @returns {number} the new zero-based page index
 */
export function stepHeroPage({ page = 0, delta = 0, pageCount = 0 } = {}) {
  if (!Number.isFinite(pageCount) || pageCount < 1) return 0;
  const raw = Number.isFinite(page) ? Math.floor(page) : 0;
  const current = Math.min(Math.max(raw, 0), pageCount - 1);
  const step = Number.isFinite(delta) ? Math.trunc(delta) : 0;
  return (((current + step) % pageCount) + pageCount) % pageCount;
}
